<template>
  <div v-if="majorError">
    <h5 class="app-failure">{{ majorError }}</h5>
  </div>
  <InfoPane
    class="dt-info-pane-sm"
    title="Network Server Info"
    v-if="networkServer"
    :actionFunc="toggleEditMode"
    actionIcon="pen-to-square"
    itemType="Network Server"
  >
    <template v-slot:info>
      <ul v-if="!inEditMode && networkServer" class="info-pane-list">

        <li class="info-pane-list-item d-flex align-items-center"  v-if="networkServer.status === 'inactive'">
          <div class="col-auto node-inactive-header">
            INACTIVE
          </div>
        </li>

        <li class="info-pane-list-item d-flex align-items-center">
          <div class="col">
            <span class="list-heading-label">
              ID:
            </span>
            {{ networkServer.id }}
          </div>
        </li>

        <li class="info-pane-list-item d-flex align-items-center"  v-if="networkServer.description">
          <div class="col">
            <span class="list-heading-label">
              Display Name:
            </span>
            {{ networkServer.description }}
          </div>
        </li>

        <li class="info-pane-list-item d-flex align-items-center"  v-if="networkServer.notes">
          <div class="col node-notes node-general-notes">
            {{ networkServer.notes }}
          </div>
        </li>

        <li class="info-pane-list-item d-flex align-items-center"  v-if="networkServer.apiEndpoint">
          <div class="col node-general-header">
            API URL:
          </div>
          <div class="col-auto">
            <a v-bind:href="networkServer.apiEndpoint">{{ networkServer.apiEndpoint }}</a>
          </div>
        </li>

        <li class="info-pane-list-item d-flex align-items-center" v-if="networkServer.uiEndpoint">
          <div class="col node-general-header">
            UI URL:
          </div>
          <div class="col-auto">
            <a v-bind:href="networkServer.uiEndpoint">{{ networkServer.uiEndpoint }}</a>
          </div>
        </li>

        <li class="info-pane-list-item" v-if="networkServer.lastModified">
          <span class="subtle-info status-last-modified">
            {{ getSinceSecs(networkServer.lastModified) }}
          </span>
          <span class="subtle-info font-reduce-2" v-if="networkServer.lastModifiedBy"> by {{ networkServer.lastModifiedBy }}</span>
        </li>

      </ul>

    <!-- END DISPLAY MODE, START EDIT FORM BELOW -->
      <form @submit.prevent="handleSubmit" class="node-form no-border" autocomplete="off" v-else>
        <div class="row form-group">
          <label class="col-form-label form-left-header" for="status">Status</label>
          <div class="col-auto form-right-input">
            <select class="form-select" v-model="networkServer.status" style="width: 100px" required>
              <GeneralStatusOptions />
            </select>
          </div>
        </div>

        <div class="row form-group" style="display:none;">
          <label class="col-form-label form-left-header" for="cascade"></label>
          <div class="col-auto form-right-input">
            <input type="checkbox" class="form-check-input" id="cascade"/>
          </div>
        </div>

        <div class="row form-group">
          <label class="col-form-label form-left-header" for="description">Description</label>
          <div class="col-auto form-right-input">
            <input
              type="text"
              id="description"
              v-model="networkServer.description"
              name="description"
              style="width:240px;"
              placeholder="Proper Name, Website, etc."
              maxlength="48"
            />
          </div>
        </div>

        <div class="row form-group">
          <label class="col-form-label form-left-header" for="notes">Notes</label>
          <div class="col-auto form-right-input">
            <textarea
              id="notes"
              v-model="networkServer.notes"
              name="notes"
              class="notes-box"
              placeholder="Enter any info about this Network Server that Agrology may find useful in the future."
            />
          </div>
        </div>

        <hr />

        <div class="row form-group">
          <label class="col-form-label form-left-header" for="apiEndpoint">API URL</label>
          <div class="col-auto form-right-input">
            <input
              type="text"
              id="apiEndpoint"
              v-model="networkServer.apiEndpoint"
              name="apiEndpoint"
              style="width:240px;"
              placeholder="https://api.example.com/"
              maxlength="64"
            />
          </div>
        </div>

        <div class="row form-group">
          <label class="col-form-label form-left-header" for="uiEndpoint">UI URL</label>
          <div class="col-auto form-right-input">
            <input
              type="text"
              id="uiEndpoint"
              v-model="networkServer.uiEndpoint"
              name="uiEndpoint"
              style="width:240px;"
              placeholder="https://ui.example.com/"
              maxlength="64"
            />
          </div>
        </div>

        <div class="row-auto mx-0 px-0 form-button-row">
          <div class="col-auto buttons-right">
            <button class="btn" :disabled="isLoading">Update</button>
            <button class="btn btn-blue" @click.prevent="handleCancel()">Cancel</button>
          </div>
        </div>

      </form>

    </template>
  </InfoPane>
</template>

<script>
import BaseFleetComponent from '@/components/fleet/BaseFleetComponent.vue'
import FleetDataService from '@/services/fleet/FleetDataService'
import GeneralStatusOptions from '@/components/selectOptions/GeneralStatusOptions.vue'
import InfoPane from '@/components/tables/InfoPane.vue'
import HelpContent from '@/services/HelpContent'

export default {
  name: 'networkServer-info',
  extends: BaseFleetComponent,
  data () {
    return {
      networkServerBeforeEdit: {},
      inEditMode: false
    }
  },
  methods: {
    toggleEditMode () {
      this.inEditMode = !this.inEditMode
    },
    getNetworkServer (id) {
      FleetDataService.getNetworkServer(id)
        .then(response => {
          this.networkServer = response.data
          this.networkServerBeforeEdit = Object.assign({}, this.networkServer)
          this.$state.creationName = this.networkServer.id
        })
        .catch(e => {
          this.majorError = ''
          this.handleError(e, 'Network Server')
        })
    },
    handleSubmit () {
      this.isLoading = true
      FleetDataService.updateNetworkServer(this.networkServer.id, this.networkServer)
        .then(() => {
          HelpContent.setTimedFlashMessage('Network Server Updated')
          this.setNavNetworkServer(this.$route.params.id)
          this.getNetworkServer(this.networkServer.id)
          this.inEditMode = false
        })
        .catch(e => {
          let message = 'Network Server Update Failed'
          if (e.response && e.response.data && e.response.data.message) {
            message += `: ${e.response.data.message}`
          }
          HelpContent.setFlashMessage(message, true)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleCancel () {
      Object.assign(this.networkServer, this.networkServerBeforeEdit)
      this.inEditMode = false
    }
  },
  mounted () {
    this.getNetworkServer(this.$route.params.id)
    this.setNavNetworkServer(this.$route.params.id)
  },
  components: { GeneralStatusOptions, InfoPane }
}
</script>

<style>
  .btn-blue {
    margin-left: 20px;
  }
</style>
